import type { Observable } from "knockout";
import { Color } from "@/lib/utils/color";
import ko from "knockout";

import $ from "jquery";

let hexInputSeq = 0;

function parseHexRgb(hex: string): [number, number, number] | null {
   if (!Color.isHexColor(hex)) {
      return null;
   }
   return [
      Number.parseInt(hex.slice(1, 3), 16),
      Number.parseInt(hex.slice(3, 5), 16),
      Number.parseInt(hex.slice(5, 7), 16),
   ];
}

function hexToHsv(hex: string): { h: number; s: number; v: number } | null {
   const rgb = parseHexRgb(hex);
   if (!rgb) {
      return null;
   }
   const r = rgb[0] / 255;
   const g = rgb[1] / 255;
   const b = rgb[2] / 255;
   const max = Math.max(r, g, b);
   const min = Math.min(r, g, b);
   const d = max - min;
   let h = 0;
   if (d !== 0) {
      if (max === r) {
         h = ((g - b) / d) % 6;
      } else if (max === g) {
         h = (b - r) / d + 2;
      } else {
         h = (r - g) / d + 4;
      }
      h *= 60;
      if (h < 0) {
         h += 360;
      }
   }
   return { h, s: max === 0 ? 0 : d / max, v: max };
}

function hsvToHex(h: number, s: number, v: number): string {
   const c = v * s;
   const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
   const m = v - c;
   let r = 0;
   let g = 0;
   let b = 0;
   if (h < 60) {
      r = c;
      g = x;
   } else if (h < 120) {
      r = x;
      g = c;
   } else if (h < 180) {
      g = c;
      b = x;
   } else if (h < 240) {
      g = x;
      b = c;
   } else if (h < 300) {
      r = x;
      b = c;
   } else {
      r = c;
      b = x;
   }
   const toHex = (n: number) =>
      Math.round((n + m) * 255)
         .toString(16)
         .padStart(2, "0");
   return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function rgbCssToHex(rgb: string): string {
   const match = /rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?/.exec(rgb);
   if (!match) {
      return "";
   }
   // An unstyled swatch computes to `rgba(0, 0, 0, 0)`, which would otherwise be labeled as black.
   if (match[4] != null && Number(match[4]) === 0) {
      return "";
   }
   const toHex = (n: string) => Number(n).toString(16).padStart(2, "0");
   return `#${toHex(match[1])}${toHex(match[2])}${toHex(match[3])}`;
}

function swatchHex(el: HTMLElement): string {
   const titled = el.getAttribute("title") || el.dataset.color || "";
   if (titled.startsWith("#")) {
      return titled;
   }
   const inner = el.querySelector(".sp-thumb-inner") as HTMLElement | null;
   const source = inner || el;
   const bg = source.style.backgroundColor || getComputedStyle(source).backgroundColor;
   return rgbCssToHex(bg);
}

function shiftHue(hex: string, delta: number): string {
   const hsv = hexToHsv(hex) || { h: 0, s: 1, v: 1 };
   const nextHue = (hsv.h + delta + 360) % 360;
   return hsvToHex(nextHue, hsv.s, hsv.v);
}

function syncSpectrumA11y(container: HTMLElement, hex: string | null): void {
   const hueEl = container.querySelector(".sp-hue") as HTMLElement | null;
   if (hueEl) {
      const hsv = hex ? hexToHsv(hex) : null;
      hueEl.setAttribute("aria-valuenow", String(Math.round(hsv?.h ?? 0)));
      if (hex && Color.isHexColor(hex)) {
         hueEl.setAttribute("aria-valuetext", hex);
      }
   }

   const thumbs = Array.from(container.querySelectorAll(".sp-thumb-el")) as HTMLElement[];
   thumbs.forEach((thumb) => {
      thumb.setAttribute("role", "radio");
      const value = swatchHex(thumb);
      if (value) {
         thumb.setAttribute("aria-label", value);
         thumb.setAttribute("title", value);
      }
      const selected = thumb.classList.contains("sp-thumb-active");
      thumb.setAttribute("aria-checked", selected ? "true" : "false");
      thumb.tabIndex = selected ? 0 : -1;
   });
   if (thumbs.length && !thumbs.some((thumb) => thumb.tabIndex === 0)) {
      thumbs[0].tabIndex = 0;
   }

   const input = container.querySelector(".sp-input") as HTMLInputElement | null;
   if (input && hex && Color.isHexColor(hex)) {
      input.setAttribute("aria-valuetext", hex);
   }
}

function enhanceSpectrumA11y(
   container: HTMLElement,
   $input: JQuery,
   currentHex: () => string | null,
   applyHex: (hex: string, commit: boolean) => void,
): void {
   if (container.dataset.spectrumA11y === "true") {
      syncSpectrumA11y(container, currentHex());
      return;
   }
   container.dataset.spectrumA11y = "true";

   const input = container.querySelector(".sp-input") as HTMLInputElement | null;
   if (input) {
      hexInputSeq += 1;
      const inputId = `spectrum-hex-input-${hexInputSeq}`;
      input.id = inputId;
      input.setAttribute("aria-label", "Hex color code");
      if (!container.querySelector(".sp-hex-input-label")) {
         const label = document.createElement("label");
         label.className = "sp-hex-input-label";
         label.htmlFor = inputId;
         label.textContent = "Hex color code";
         input.parentElement?.insertBefore(label, input);
      }
   }

   const hueEl = container.querySelector(".sp-hue") as HTMLElement | null;
   if (hueEl) {
      hueEl.setAttribute("role", "slider");
      hueEl.setAttribute("tabindex", "0");
      hueEl.setAttribute("aria-label", "Color hue");
      hueEl.setAttribute("aria-valuemin", "0");
      hueEl.setAttribute("aria-valuemax", "360");
      hueEl.setAttribute("aria-orientation", "vertical");
      hueEl.addEventListener("keydown", (event: KeyboardEvent) => {
         const hex = currentHex();
         if (!hex) {
            return;
         }
         let next: string | null = null;
         if (event.key === "ArrowUp" || event.key === "ArrowRight") {
            next = shiftHue(hex, 5);
         } else if (event.key === "ArrowDown" || event.key === "ArrowLeft") {
            next = shiftHue(hex, -5);
         } else if (event.key === "Home") {
            const hsv = hexToHsv(hex) || { h: 0, s: 1, v: 1 };
            next = hsvToHex(0, hsv.s, hsv.v);
         } else if (event.key === "End") {
            const hsv = hexToHsv(hex) || { h: 0, s: 1, v: 1 };
            next = hsvToHex(359, hsv.s, hsv.v);
         }
         if (next === null) {
            return;
         }
         event.preventDefault();
         ($input as any).spectrum("set", next);
         applyHex(next, false);
         syncSpectrumA11y(container, next);
      });
   }

   const palettes = Array.from(container.querySelectorAll(".sp-palette")) as HTMLElement[];
   palettes.forEach((palette) => {
      palette.setAttribute("role", "radiogroup");
      palette.setAttribute("aria-label", "Color palette");
   });

   const thumbs = Array.from(container.querySelectorAll(".sp-thumb-el")) as HTMLElement[];
   thumbs.forEach((thumb, index) => {
      thumb.setAttribute("role", "radio");
      thumb.addEventListener("keydown", (event: KeyboardEvent) => {
         const moveTo = (nextIndex: number) => {
            const next = thumbs[nextIndex];
            const hex = swatchHex(next);
            next.focus();
            if (hex) {
               ($input as any).spectrum("set", hex);
               applyHex(hex, false);
               syncSpectrumA11y(container, hex);
            }
         };
         if (event.key === "ArrowRight" || event.key === "ArrowDown") {
            event.preventDefault();
            moveTo((index + 1) % thumbs.length);
         } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
            event.preventDefault();
            moveTo((index - 1 + thumbs.length) % thumbs.length);
         } else if (event.key === "Home") {
            event.preventDefault();
            moveTo(0);
         } else if (event.key === "End") {
            event.preventDefault();
            moveTo(thumbs.length - 1);
         } else if (event.key === " " || event.key === "Enter") {
            event.preventDefault();
            thumb.click();
         }
      });
   });

   syncSpectrumA11y(container, currentHex());
}

ko.bindingHandlers["colorWheel2"] = {
   init: (
      element,
      valueAccessor: () => {
         value: Observable<string | null>;
         onSelected?: (newColor: string) => void;
         hideButtons?: boolean;
      },
   ) => {
      const hexVal = valueAccessor().value;
      const onSelected = valueAccessor().onSelected;
      const hideButtons = valueAccessor().hideButtons || false;
      const defaultColor = ko.unwrap(hexVal) ?? "#34FFA9";
      const $picker = $(element).find(".color-picker") as any;

      const currentHex = () => hexVal();

      const applyA11y = () => {
         // The vendored spectrum plugin defaults to `appendTo: "body"`, so the
         // `.sp-container` popup is not a DOM descendant of `element` - it must be
         // looked up via spectrum's own "container" API to get the right instance.
         const container = ($picker.spectrum("container") as JQuery | undefined)?.[0];
         if (container) {
            enhanceSpectrumA11y(container, $picker, currentHex, (hex, commit) => {
               hexVal(hex);
               if (commit && onSelected != undefined) {
                  onSelected(hex);
               }
            });
         }
      };

      const updateColor = function (newColor: { toHexString: () => string }) {
         if (Color.isHexColor(newColor.toHexString())) {
            hexVal(newColor.toHexString());
            if (onSelected != undefined) {
               onSelected(newColor.toHexString());
            }
         }
         applyA11y();
      };

      const handleMove = function (newColor: { toHexString: () => string }) {
         if (hideButtons && Color.isHexColor(newColor.toHexString())) {
            hexVal(newColor.toHexString());
            if (onSelected != undefined) {
               onSelected(newColor.toHexString());
            }
         }
         applyA11y();
      };

      const subscription = hexVal.subscribe((value: string | null) => {
         if (Color.isHexColor(value)) {
            $picker.spectrum("set", value);
            applyA11y();
         }
      });

      const spectrumOptions: { [key: string]: any } = {
         type: "flat",
         showInput: true,
         showInitial: true,
         showAlpha: false,
         showButtons: !hideButtons,
         allowEmpty: false,
         change: updateColor,
         move: handleMove,
         clickoutFiresChange: false,
      };

      const colorPicker = $picker.spectrum(spectrumOptions);

      const initialValue = Color.isHexColor(hexVal()) ? hexVal() : defaultColor;
      $picker.spectrum("set", initialValue);
      applyA11y();
      globalThis.setTimeout(applyA11y, 0);

      return ko.utils.domNodeDisposal.addDisposeCallback(colorPicker, () => {
         return subscription.dispose();
      });
   },
};
